import { FilledButton } from "@/components/ui/buttons";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { memo } from "react";

interface SettingModalHeaderProps {
  title: string;
  onBack?: () => void;
  onSave?: () => void;
  isSaving?: boolean;
  showSaveButton?: boolean;
  isModal?: boolean;
  className?: string;
}

export const SettingModalHeader = memo(function SettingModalHeader({
  title,
  onBack,
  onSave,
  isSaving = false,
  showSaveButton = false,
  isModal = false,
  className,
}: SettingModalHeaderProps) {
  if (isModal) {
    return (
      <div
        className={cn(
          "relative sticky top-0 z-10 flex items-center justify-between bg-[#fcfbfb] pl-5 pb-3 pr-5",
          className,
        )}
        style={{ paddingTop: "max(64px, calc(env(safe-area-inset-top) + 18px))" }}
      >
        <div className="w-[46px]">
          {onBack && (
            <button
              type="button"
              aria-label="Close"
              onClick={onBack}
              className="group relative flex h-[46px] w-[46px] cursor-pointer items-center justify-center"
            >
              <div className="absolute size-10 rounded-full bg-[#ebe0fb] opacity-40 group-active:bg-[#d4b8f7] group-active:opacity-60" aria-hidden="true" />
              <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" className="relative z-10" aria-hidden="true" style={{ paddingTop: '1px' }}>
                <path d="M1 1.5L6 6L11 1.5" stroke="#620AD5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
        </div>
        <h2 className={cn(textVariants.title3, "pointer-events-none absolute inset-x-0 text-center text-black")}>{title}</h2>
        <div className="w-[84px]">
          {showSaveButton && onSave && (
            <FilledButton
              text="Save"
              color="brand"
              textCase="normal"
              className="h-[46px] w-[84px]"
              disabled={isSaving}
              onClick={onSave}
            />
          )}
        </div>
      </div>
    );
  }

  // Page header (non-modal): Figma design — back chevron + title left-aligned
  // Top padding: at least 54px (status bar) + 18px = 72px, or the device safe area + 18px if larger.
  return (
    <div
      className={cn(
        "sticky top-0 z-10 flex items-center justify-between bg-[#fcfbfb] pb-2 pr-7",
        onBack ? "pl-[19px]" : "pl-8",
        className,
      )}
      style={{ paddingTop: "max(64px, calc(env(safe-area-inset-top) + 18px))" }}
    >
      <div className="flex items-center gap-3">
        {onBack && (
          <button
            type="button"
            aria-label="Go back"
            onClick={onBack}
            className="group relative flex h-[46px] w-[46px] shrink-0 cursor-pointer items-center justify-center"
          >
            <div className="absolute size-10 rounded-full bg-[#ebe0fb] opacity-40 group-active:bg-[#d4b8f7] group-active:opacity-60" aria-hidden="true" />
            <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg" className="relative z-10" aria-hidden="true">
              <path d="M6.5 1L2 6L6.5 11" stroke="#620AD5" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}
        <h2
          className={cn(
            textVariants.serif,
            "text-[32px] leading-[45px] font-semibold tracking-[-1px] text-[#1d1232]",
          )}
        >
          {title}
        </h2>
      </div>
      {showSaveButton && onSave && (
        <FilledButton
          text="Save"
          color="brand"
          textCase="normal"
          className="h-[46px] w-[84px] shrink-0"
          disabled={isSaving}
          onClick={onSave}
        />
      )}
    </div>
  );
});
